import {
  AssistantCitation,
  AssistantClarificationEvent,
  AssistantPart,
} from '@services/modules/AssistantStream'

/*
 * One assistant turn as it streams in. Pure reducer so the whole stream protocol can be
 * tested without a network or a store: SSE events → TurnEvent → turnReducer → TurnState,
 * and a finished turn becomes a message with turnToMessage.
 */

export type TurnEvent =
  | { type: 'token'; text: string }
  | { type: 'status'; label: string }
  | { type: 'part'; part: AssistantPart }
  | { type: 'citations'; items: AssistantCitation[] }
  | { type: 'clarification'; clarification: AssistantClarificationEvent }

export interface TurnState {
  /** Answer text streamed so far. */
  content: string
  /** Progress label while a tool runs ("Searching cases…"); cleared by the next text. */
  status: string | null
  parts: AssistantPart[]
  citations: AssistantCitation[]
  clarification: AssistantClarificationEvent | null
}

export const initialTurn = (): TurnState => ({
  content: '',
  status: null,
  parts: [],
  citations: [],
  clarification: null,
})

export const turnReducer = (state: TurnState, event: TurnEvent): TurnState => {
  switch (event.type) {
    case 'token':
      return { ...state, content: state.content + event.text, status: null }
    case 'status':
      return { ...state, status: event.label || null }
    case 'part':
      return { ...state, parts: [...state.parts, event.part] }
    case 'citations':
      return { ...state, citations: event.items }
    case 'clarification':
      return { ...state, clarification: event.clarification, status: null }
    default:
      return state
  }
}

export interface TurnMessage {
  id?: string
  role: 'Assistant'
  content: string
  citations?: AssistantCitation[]
  clarification?: AssistantClarificationEvent
  parts?: AssistantPart[]
  partial?: boolean
}

/** The message a turn leaves behind — finished (with an id) or cut short (partial). */
export const turnToMessage = (
  state: TurnState,
  messageId?: string
): TurnMessage | null => {
  if (state.clarification && messageId) {
    return {
      id: messageId,
      role: 'Assistant',
      content: state.clarification.question,
      clarification: { ...state.clarification, messageId },
    }
  }

  if (!messageId && !state.content) {
    return null
  }

  return {
    id: messageId,
    role: 'Assistant',
    content: state.content,
    citations: state.citations.length ? state.citations : undefined,
    parts: state.parts.length ? state.parts : undefined,
    partial: messageId ? undefined : true,
  }
}
